import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
} from '@/graphql/generatedTypes';
import type { OrderSystemType } from './orderListFilters';

type TransitionEntry =
  | MerchantOrderStatusEnum
  | { status: MerchantOrderStatusEnum; hideInDropdown?: boolean };

type TransitionMap = Partial<
  Record<MerchantOrderStatusEnum, TransitionEntry[]>
>;

const OPEN_SYSTEM_TRANSITIONS: TransitionMap = {
  [MerchantOrderStatusEnum.OrderReceived]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.Approved]: [
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
    { status: MerchantOrderStatusEnum.SentToPicking, hideInDropdown: true },
  ],
  [MerchantOrderStatusEnum.SentToPicking]: [MerchantOrderStatusEnum.Canceled],
  [MerchantOrderStatusEnum.Picked]: [
    { status: MerchantOrderStatusEnum.ReturnToSender, hideInDropdown: true },
  ],
  [MerchantOrderStatusEnum.InTransit]: [
    { status: MerchantOrderStatusEnum.ReturnToSender, hideInDropdown: true },
  ],
  [MerchantOrderStatusEnum.Delivered]: [
    { status: MerchantOrderStatusEnum.ReturnToSender, hideInDropdown: true },
    { status: MerchantOrderStatusEnum.Completed, hideInDropdown: true },
  ],
  // The only ways out are the replacement mutation (RETURN_TO_SENDER → PICKED)
  // and the picking job's resolvePickingJob, which cancels the order as part
  // of returning its stock to the warehouse. Neither is a status change: the
  // replacement has to announce a new parcel set to Sendcloud first, and a bare
  // cancel would leave the returned parcels and the picked stock unaccounted
  // for — which is why CANCELED is deliberately absent here. Its absence also
  // takes the cancel action off the list row, whose menu consults this map.
  [MerchantOrderStatusEnum.ReturnToSender]: [
    { status: MerchantOrderStatusEnum.Picked, hideInDropdown: true },
  ],
  [MerchantOrderStatusEnum.Invalid]: [
    MerchantOrderStatusEnum.OrderReceived,
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.Canceled]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.OnHold]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
  ],
  [MerchantOrderStatusEnum.Error]: [
    MerchantOrderStatusEnum.OrderReceived,
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
    // Written by the delivery poller when a newer delivery event outranks the
    // failure, and by a picking job's resolution to the PARTNER. Never a
    // dropdown choice: an admin declaring an errored order delivered would have
    // no evidence and, since booking is decoupled, no effect on the stock.
    { status: MerchantOrderStatusEnum.Delivered, hideInDropdown: true },
    { status: MerchantOrderStatusEnum.ReturnToSender, hideInDropdown: true },
  ],
};

const EVENT_TRANSITIONS: TransitionMap = {
  [MerchantOrderStatusEnum.OrderReceived]: [MerchantOrderStatusEnum.Canceled], // TODO: What about the form then?
  [MerchantOrderStatusEnum.PendingApproval]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.Approved]: [
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.SalesOrderSent]: [
    MerchantOrderStatusEnum.SentToPicking,
  ],
  [MerchantOrderStatusEnum.SentToPicking]: [],
  [MerchantOrderStatusEnum.Picked]: [],
  [MerchantOrderStatusEnum.OnHold]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
  ],
  [MerchantOrderStatusEnum.Canceled]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.OnHold,
  ],
  [MerchantOrderStatusEnum.Error]: [
    MerchantOrderStatusEnum.Approved,
    MerchantOrderStatusEnum.Canceled,
    MerchantOrderStatusEnum.OnHold,
  ],
};

const TRANSITION_MAPS: Record<OrderSystemType, TransitionMap> = {
  OPEN_SYSTEM: OPEN_SYSTEM_TRANSITIONS,
  EVENT: EVENT_TRANSITIONS,
};

const RETURN_TRANSITIONS: TransitionMap = {
  ...OPEN_SYSTEM_TRANSITIONS,
  [MerchantOrderStatusEnum.Canceled]: [],
};

const TYPE_TRANSITION_OVERRIDES: Partial<
  Record<MerchantOrderTypeEnum, TransitionMap>
> = {
  [MerchantOrderTypeEnum.DpdPickup]: RETURN_TRANSITIONS,
  [MerchantOrderTypeEnum.DpdShopReturn]: RETURN_TRANSITIONS,
  [MerchantOrderTypeEnum.EventBufferOrder]: {
    ...EVENT_TRANSITIONS,
    [MerchantOrderStatusEnum.Approved]: [
      MerchantOrderStatusEnum.SentToPicking,
      MerchantOrderStatusEnum.Canceled,
      MerchantOrderStatusEnum.OnHold,
    ],
  },
};

function resolveStatus(entry: TransitionEntry): MerchantOrderStatusEnum {
  return typeof entry === 'string' ? entry : entry.status;
}

function getTransitionMap(
  system: string,
  orderType?: MerchantOrderTypeEnum | null,
): TransitionMap {
  return (
    (orderType && TYPE_TRANSITION_OVERRIDES[orderType]) ??
    (TRANSITION_MAPS as Record<string, TransitionMap>)[system] ??
    {}
  );
}

function getAllNextStatuses(
  system: string,
  currentStatus: MerchantOrderStatusEnum,
  orderType?: MerchantOrderTypeEnum | null,
): MerchantOrderStatusEnum[] {
  const entries = getTransitionMap(system, orderType)[currentStatus] ?? [];
  return entries.map(resolveStatus);
}

export function getValidNextStatuses(
  system: string,
  currentStatus: MerchantOrderStatusEnum,
  orderType?: MerchantOrderTypeEnum | null,
): MerchantOrderStatusEnum[] {
  const entries = getTransitionMap(system, orderType)[currentStatus] ?? [];
  return entries
    .filter((e) => typeof e === 'string' || !e.hideInDropdown)
    .map(resolveStatus);
}

export function canTransitionTo(
  system: string,
  currentStatus: MerchantOrderStatusEnum,
  targetStatus: MerchantOrderStatusEnum,
  orderType?: MerchantOrderTypeEnum | null,
): boolean {
  return getAllNextStatuses(system, currentStatus, orderType).includes(
    targetStatus,
  );
}
